/**
 * 매장 상세 페이지 액션 핸들러 (제품 담기/구매/예약/좋아요/알림/수량 확정).
 * page는 이 hook의 반환값만 사용하고 인라인 핸들러를 두지 않는다.
 */
import { useCallback } from "react";
import { track } from "@amplitude/analytics-browser";
import { isWeb } from "../../../utils/user-agent";
import {
  getReserveTime,
  getReserveTimeForTomorrow,
} from "../../../utils/reserve-time";
import { calculateReserveTimeBefore30Minutes } from "../utils/store-time";
import type { SelectedProductForQuantity } from "./use-store-page-state";

export interface StoreActionsOptions {
  store:
    | {
        id?: number;
        title?: string | null;
        closingHours?: string | null;
        address?: string | null;
        reserveTime?: Record<string, { start?: string; end?: string }> | null;
        todaysProducts?: Array<{
          id: number;
          name: string;
          price: number;
          userPrice?: number | null;
          discountPrice?: number | null;
          description?: string | null;
          createdAt?: string | null;
          updatedAt?: string | null;
          saleEndTime?: string | null;
          img?: string | null;
          quantity?: number | null;
          currentCount?: number | null;
          menus?: unknown;
        }> | null;
      }
    | null
    | undefined;
  cart: Array<{ product: { id: number } }>;
  addToCart: (product: unknown, quantity: number, storeInfo: unknown) => void;
  removeFromCart: (productId: number) => void;
  isLoggedIn: boolean;
  navigate: (path: string) => void;
  likeStore: (options: { variables: { storeId: number } }) => Promise<unknown>;
  cancelLike: (options: { variables: { storeId: number } }) => Promise<unknown>;
  setIsLoginSheetOpen: (open: boolean) => void;
  setSelectedProductForQuantity: (
    value: SelectedProductForQuantity | null,
  ) => void;
  setIsQuantitySheetOpen: (open: boolean) => void;
  setIsReserveConfirmOpen: (open: boolean) => void;
  setIsTomorrowForConfirm: (isTomorrow: boolean) => void;
  selectedProductForQuantity: SelectedProductForQuantity | null;
  setIsSheetOpen: (open: boolean) => void;
  totalAmount: number;
}

/** 제품 클릭 시 전달되는 최소 shape (todaysProducts 요소 또는 유사) */
export interface ProductForStoreAction {
  id: number;
  name: string;
  price: number;
  userPrice?: number | null;
  currentCount?: number | null;
  description?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  saleEndTime?: string | null;
  img?: string | null;
  discountPrice?: number | null;
  quantity?: number | null;
  menus?: unknown;
}

export function useStoreActions(options: StoreActionsOptions) {
  const {
    store,
    cart,
    addToCart,
    removeFromCart,
    isLoggedIn,
    navigate,
    likeStore,
    cancelLike,
    setIsLoginSheetOpen,
    setSelectedProductForQuantity,
    setIsQuantitySheetOpen,
    setIsReserveConfirmOpen,
    setIsTomorrowForConfirm,
    selectedProductForQuantity,
    setIsSheetOpen,
    totalAmount,
  } = options;

  const onClickProduct = useCallback(
    (product: ProductForStoreAction, isAddToCart = false) => {
      if (isAddToCart) {
        if (!isLoggedIn) {
          setIsLoginSheetOpen(true);
          return;
        }
        const existingItem = cart.find(
          (item) => item.product.id === product.id,
        );
        if (existingItem) {
          track("제품 클릭(담기 취소)", {
            매장명: store?.title as string,
            제품명: product.name,
          });
          removeFromCart(product.id);
          return;
        }
        track("제품 클릭(담기)", {
          매장명: store?.title as string,
          제품명: product.name,
          가격: product.userPrice,
        });
        const targetProduct = store?.todaysProducts?.find(
          (p) => p.id === product.id,
        );
        const currentCount =
          targetProduct?.currentCount ?? product.currentCount ?? 1;
        if (currentCount >= 2) {
          setSelectedProductForQuantity({
            id: product.id,
            name: product.name,
            price: product.price,
            userPrice: product.userPrice ?? product.price,
            currentCount,
          });
          setIsQuantitySheetOpen(true);
          return;
        }
        const productToAdd = targetProduct ?? product;
        const productForCart = {
          ...productToAdd,
          description: productToAdd.description ?? "",
          createdAt: productToAdd.createdAt ?? "",
          updatedAt: productToAdd.updatedAt ?? "",
          saleEndTime: productToAdd.saleEndTime ?? "",
          img: productToAdd.img ?? "",
          discountPrice: Number(productToAdd.discountPrice ?? 0),
          userPrice: Number(productToAdd.userPrice ?? 0),
          price: Number(productToAdd.price ?? 0),
          quantity: Number(productToAdd.quantity ?? 0),
          store: { id: store?.id as number },
          menus: productToAdd.menus ?? null,
        };
        addToCart(productForCart, 1, {
          id: store?.id as number,
          closingHours: store?.closingHours ?? "",
          address: store?.address ?? "",
          title: store?.title as string,
        });
      } else {
        track("제품 클릭(상세 페이지 이동)", {
          매장명: store?.title as string,
          제품명: product.name,
          가격: product.userPrice,
        });
        navigate(`/product/${product.id}`);
      }
    },
    [
      isLoggedIn,
      cart,
      store,
      removeFromCart,
      setIsLoginSheetOpen,
      setSelectedProductForQuantity,
      setIsQuantitySheetOpen,
      addToCart,
      navigate,
    ],
  );

  const onClickPurchage = useCallback(() => {
    if (!isLoggedIn) {
      setIsLoginSheetOpen(true);
      return;
    }
    track("구매하기(카트이동) 버튼 클릭", { 총액: totalAmount });
    navigate(
      `/live-order/intro/${store?.id}?closingHours=${encodeURIComponent(store?.closingHours ?? "")}`,
    );
  }, [
    isLoggedIn,
    totalAmount,
    setIsLoginSheetOpen,
    navigate,
    store?.id,
    store?.closingHours,
  ]);

  const onClickPackPurchase = useCallback(
    (isTomorrow: boolean) => {
      // if (isWeb()) {
      //   setIsSheetOpen(true);
      //   return;
      // }
      if (!isLoggedIn) {
        setIsLoginSheetOpen(true);
        return;
      }
      setIsTomorrowForConfirm(isTomorrow);
      setIsReserveConfirmOpen(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isLoggedIn,
      setIsSheetOpen,
      setIsLoginSheetOpen,
      setIsTomorrowForConfirm,
      setIsReserveConfirmOpen,
    ],
  );

  const executePackPurchase = useCallback(
    (isTomorrow: boolean) => {
      const reserveTimeBefore30Minutes = calculateReserveTimeBefore30Minutes(
        0,
        store?.reserveTime as
          | Record<string, { start?: string; end?: string }>
          | null
          | undefined,
      );
      const tomorrowReserveTimeBefore30Minutes =
        calculateReserveTimeBefore30Minutes(
          1,
          store?.reserveTime as
            | Record<string, { start?: string; end?: string }>
            | null
            | undefined,
        );
      const time = isTomorrow
        ? tomorrowReserveTimeBefore30Minutes
        : reserveTimeBefore30Minutes;
      const reserveTimeStr = isTomorrow
        ? getReserveTimeForTomorrow(store?.reserveTime)
        : getReserveTime(store?.reserveTime);
      navigate(
        `/pre-order/intro/${store?.id}?time=${time}&isTomorrow=${isTomorrow}&reserveTime=${reserveTimeStr}`,
      );
    },
    [store?.id, store?.reserveTime, navigate],
  );

  const onClickLike = useCallback(
    (storeId: number, isLiked: boolean | null): Promise<void> => {
      if (isWeb()) {
        setIsSheetOpen(true);
        return Promise.resolve();
      }
      track(isLiked ? "좋아요취소 클릭" : "좋아요 클릭", {
        어디서: "매장상세",
        매장명: store?.title as string,
      });
      if (isLiked) {
        return cancelLike({ variables: { storeId } }).then(() => undefined);
      }
      return likeStore({ variables: { storeId } }).then(() => undefined);
    },
    [store?.title, setIsSheetOpen, cancelLike, likeStore],
  );

  const onClickNoti = useCallback(
    (
      storeId: number,
      isLiked: boolean | null,
      storeTitle?: string,
    ): Promise<void> => {
      if (isWeb()) {
        setIsSheetOpen(true);
        return Promise.resolve();
      }
      track(isLiked ? "알림취소 클릭" : "알림받기 클릭", {
        어디서: "매장상세",
        매장명: storeTitle,
      });
      if (isLiked) {
        return cancelLike({ variables: { storeId } }).then(() => undefined);
      }
      return likeStore({ variables: { storeId } }).then(() => undefined);
    },
    [setIsSheetOpen, cancelLike, likeStore],
  );

  const handleQuantityConfirm = useCallback(
    (quantity: number) => {
      if (!selectedProductForQuantity) return;
      const productToAdd = store?.todaysProducts?.find(
        (p) => p.id === selectedProductForQuantity.id,
      );
      if (productToAdd) {
        const productForCart = {
          ...productToAdd,
          description: "",
          createdAt: "",
          updatedAt: "",
          saleEndTime: productToAdd.saleEndTime ?? "",
          img: productToAdd.img ?? "",
          store: { id: store?.id as number },
          menus: productToAdd.menus ?? null,
        };
        addToCart(productForCart, quantity, {
          id: store?.id as number,
          closingHours: store?.closingHours ?? "",
          address: store?.address ?? "",
          title: store?.title as string,
        });
      }
      setIsQuantitySheetOpen(false);
      setSelectedProductForQuantity(null);
    },
    [
      selectedProductForQuantity,
      store,
      addToCart,
      setSelectedProductForQuantity,
      setIsQuantitySheetOpen,
    ],
  );

  return {
    onClickProduct,
    onClickPurchage,
    onClickPackPurchase,
    executePackPurchase,
    onClickLike,
    onClickNoti,
    handleQuantityConfirm,
  };
}
